import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/utils/cn';
import { Spinner } from './Spinner';

type IconButtonSize = 'xs' | 'sm' | 'md';

/**
 * 'outline' stands on its own beside a field. 'bare' sits inside another bordered control — the
 * clear and search affordances tucked into a lookup box — where a second border would draw a box
 * inside a box.
 */
type IconButtonVariant = 'outline' | 'bare';

const variantClasses: Record<IconButtonVariant, string> = {
  outline:
    'rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 '
    + 'text-gray-500 dark:text-gray-400 hover:text-brand-600 hover:border-brand-400',
  bare:
    'bg-transparent text-gray-400 hover:text-brand-600 rounded-md',
};

const sizeClasses: Record<IconButtonSize, string> = {
  xs: 'h-7 w-7',
  sm: 'h-8 w-8',
  md: 'h-9 w-9',
};

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Required: the control shows an icon, so the name has to be given to a screen reader. */
  label: string;
  icon: ReactNode;
  loading?: boolean;
  size?: IconButtonSize;
  variant?: IconButtonVariant;
}

/**
 * A square button carrying an icon and no text — the lookup and clear controls beside a code field.
 *
 * Separate from Button because Button's padding is set for a label; a square control needs equal
 * sides. `label` is not optional: seven of these existed across the master screens and none of them
 * had an accessible name, so a screen reader announced each one as "button".
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ label, icon, loading = false, size = 'sm', variant = 'outline', disabled, className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled || loading}
      className={cn(
        'flex items-center justify-center transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        'disabled:opacity-40 disabled:cursor-not-allowed',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...props}
    >
      {loading ? <Spinner size="sm" /> : icon}
    </button>
  ),
);

IconButton.displayName = 'IconButton';
